import type { QuestionImagepickerModelData } from '@coorum-research/shared-common/types'
import type { ComponentProps } from 'react'
import type { SetRequired } from 'type-fest'
import type { SurveyElementForm } from './survey-element-form'
import * as ToggleGroup from '@radix-ui/react-toggle-group'
import { cn } from '../../utils'
import { ImagepickerCheckIcon } from '../icons/ImagepickerCheckIcon'
import { ElementCommentField } from './ElementCommentField'
import { ElementHeader } from './ElementHeader'
import { ElementValidationMessage } from './ElementValidationMessage'
import { SurveyElementLayout } from './SurveyElementLayout'

type ElementChoice = SetRequired<QuestionImagepickerModelData, 'choices'>['choices'][number]

function ToggleImageItem({ ref, showCaption, choice, ...props }: ComponentProps<typeof ToggleGroup.Item> & {
  showCaption?: boolean | null
  choice: ElementChoice
}) {
  const caption = choice.text ?? choice.value

  return (
    <ToggleGroup.Item
      ref={ref}
      key={choice.value}
      {...props}
      asChild
    >
      <div className="group relative w-full space-y-1">
        <div className="relative max-w-full overflow-hidden rounded-lg border">
          <img
            src={choice.imageLink}
            alt={choice.text}
            className="mx-auto block max-w-full object-cover transition-all duration-300 hover:opacity-60"
          />
        </div>
        {showCaption && caption && (
          <div className="px-1">
            <p className="truncate text-center text-[var(--theme-title-color)]">
              {caption}
            </p>
          </div>
        )}
        <div className="absolute right-1 top-1 hidden size-12 items-center justify-center rounded-full bg-white shadow-md group-data-[state=on]:flex">
          <ImagepickerCheckIcon className="fill-[var(--theme-bg-color)]" />
        </div>
      </div>
    </ToggleGroup.Item>
  )
}

type Props = {
  currentForm: SurveyElementForm
  validationError: string
  onValidationError: (errorMessage: string) => void
  onClickNext?: () => void
}

export function SurveyElementImagepicker({
  currentForm,
  validationError,
  onValidationError,
  onClickNext,
}: Props) {
  const currentElement = currentForm.elementJson as QuestionImagepickerModelData
  const toggleGroupProp = currentElement.multiSelect
    ? {
      type: 'multiple' as const,
      defaultValue: currentForm.getValue() as string[],
      onValueChange: (value: string[]) => {
        currentForm.setElementResultRecordValue(value)
        onValidationError('')
      },
    }
    : {
      type: 'single' as const,
      defaultValue: currentForm.getValue() as string,
      onValueChange: (value: string) => {
        currentForm.setElementResultRecordValue(value)
        onValidationError('')
        if (!currentElement.showCommentArea) {
          onClickNext?.()
        }
      },
    }

  return (
    <SurveyElementLayout>
      <ElementValidationMessage
        validationError={validationError}
      />
      <ElementHeader
        currentElement={currentElement}
      />
      <ToggleGroup.Root
        className={cn(
          'grid items-start gap-2',
          currentElement.displayColumnSize === 'one' ? 'grid-cols-1' : 'grid-cols-2',
        )}
        {...toggleGroupProp}
      >
        {currentElement.choices?.map(choice => (
          <ToggleImageItem
            key={choice.value}
            value={choice.value}
            choice={choice}
            showCaption={currentElement.showCaption ?? false}
          />
        ))}
      </ToggleGroup.Root>
      {currentElement.showCommentArea && (
        <ElementCommentField
          label={currentElement.commentText}
          placeholder={currentElement.commentPlaceholder}
          defaultValue={currentForm.getComment()}
          onChange={(e) => {
            currentForm.setElementResultRecordComment(e.target.value || '')
          }}
        />
      )}
    </SurveyElementLayout>
  )
}
